/** @jsxImportSource @emotion/react */
import {
  EuiCheckbox,
  EuiFieldText,
  EuiFormRow,
  EuiSelect,
} from "@inscopix/ideas-eui";
import { useFormikContext } from "formik";
import { ProvinceCode } from "./constants/canada-provinces";
import {
  PERSONAL_SPACE_FORM_DATA_TYPE_OPTIONS,
  PERSONAL_SPACE_FORM_FIELD_NAMES_BY_KEY,
  PERSONAL_SPACE_FORM_OCCUPATION_OPTIONS,
  PersonalSpaceFormFields,
  PERSONAL_SPACE_FORM_ANALYTICS_OPTIONS,
  PERSONAL_SPACE_FORM_INTEREST_OPTIONS,
} from "./FormCreatePersonalSpace";
import { StateCode } from "./constants/us-states";
import { FormCreatePersonalSpaceBodyProps } from "./FormCreatePersonalSpaceBody";
import { Checkbox } from "./FormComponents/Checkbox";
import { CountrySelectable } from "./FormComponents/CountrySelectable";
import { StateProvinceSelectable } from "./FormComponents/StateProvinceSelectable";
import { PageNumbers } from "./FormCreatePersonalSpace.helpers";

const stateRequiredCountries = [
  "US",
  "CA",
] satisfies PersonalSpaceFormFields["country"][];

export interface FormCreatePersonalSpaceFieldsProps {
  page: PageNumbers[number];
  fieldsByPage: FormCreatePersonalSpaceBodyProps["fieldsByPage"];
}

export const FormCreatePersonalSpaceFields = ({
  page,
  fieldsByPage,
}: FormCreatePersonalSpaceFieldsProps) => {
  const { values, handleChange, handleBlur, getFieldMeta, setFieldValue } =
    useFormikContext<PersonalSpaceFormFields>();

  return (
    <div>
      {/* Form Fields */}
      {fieldsByPage.get(page)?.fields.map((fieldKey) => {
        const meta = getFieldMeta(fieldKey);
        const fieldName = PERSONAL_SPACE_FORM_FIELD_NAMES_BY_KEY[fieldKey];

        switch (fieldKey) {
          case "marketingConsent":
            return (
              <EuiFormRow
                key={fieldKey}
                title={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <Checkbox fieldKey={fieldKey} fieldName={fieldName} />
              </EuiFormRow>
            );

          case "occupation":
            return (
              <EuiFormRow
                key={fieldKey}
                title={fieldName}
                label={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <EuiSelect
                  id={fieldKey}
                  name={fieldKey}
                  title={fieldName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values[fieldKey]}
                  hasNoInitialSelection
                  options={[...PERSONAL_SPACE_FORM_OCCUPATION_OPTIONS].map(
                    (option) => ({
                      value: option.key,
                      text: option.display,
                    }),
                  )}
                />
              </EuiFormRow>
            );
          case "occupationDetail":
            if (
              getFieldMeta<PersonalSpaceFormFields["occupation"]>(
                "occupation" satisfies keyof PersonalSpaceFormFields,
              )?.value === "occupation_other"
            ) {
              return (
                <EuiFormRow
                  key={fieldKey}
                  title={fieldName}
                  label={fieldName}
                  isInvalid={meta.touched && meta.error !== undefined}
                  error={meta.error}
                  style={{ marginBottom: 10 }}
                >
                  <EuiFieldText
                    id={fieldKey}
                    title={fieldName}
                    name={fieldKey}
                    value={values[fieldKey]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </EuiFormRow>
              );
            } else {
              return null;
            }

          case "group":
          case "institution":
            return (
              <EuiFormRow
                key={fieldKey}
                title={fieldName}
                label={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <EuiFieldText
                  id={fieldKey}
                  title={fieldName}
                  name={fieldKey}
                  value={values[fieldKey]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </EuiFormRow>
            );

          case "dataType":
            return (
              <EuiFormRow
                key={fieldKey}
                label={fieldName}
                title={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <div role="group" aria-labelledby="checkbox-group">
                  {PERSONAL_SPACE_FORM_DATA_TYPE_OPTIONS.map((option) => (
                    <EuiCheckbox
                      key={fieldKey + option.key}
                      id={fieldKey + option.key}
                      label={option.display}
                      name={fieldKey}
                      value={option.key}
                      checked={values[fieldKey]?.includes(option.key)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ))}
                </div>
              </EuiFormRow>
            );

          case "dataTypeDetail":
            if (
              getFieldMeta<PersonalSpaceFormFields["dataType"]>(
                "dataType" as const satisfies keyof PersonalSpaceFormFields,
              )?.value?.includes("data_type_other")
            ) {
              return (
                <EuiFormRow
                  key={fieldKey}
                  title={fieldName}
                  hasEmptyLabelSpace
                  isInvalid={meta.touched && meta.error !== undefined}
                  error={meta.error}
                  style={{ marginBottom: 10 }}
                >
                  <EuiFieldText
                    id={fieldKey}
                    title={fieldName}
                    name={fieldKey}
                    value={values[fieldKey]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </EuiFormRow>
              );
            } else {
              return null;
            }

          case "analytics":
            return (
              <EuiFormRow
                key={fieldKey}
                label={fieldName}
                title={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <div role="group" aria-labelledby="checkbox-group">
                  {PERSONAL_SPACE_FORM_ANALYTICS_OPTIONS.map((option) => (
                    <EuiCheckbox
                      key={fieldKey + option.key}
                      id={fieldKey + option.key}
                      label={option.display}
                      name={fieldKey}
                      value={option.key}
                      checked={values[fieldKey]?.includes(option.key)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  ))}
                </div>
              </EuiFormRow>
            );

          case "analyticsDetail":
            if (
              getFieldMeta<PersonalSpaceFormFields["analytics"]>(
                "analytics" as const satisfies keyof PersonalSpaceFormFields,
              )?.value?.includes("analytics_other")
            ) {
              return (
                <EuiFormRow
                  key={fieldKey}
                  title={fieldName}
                  hasEmptyLabelSpace
                  isInvalid={meta.touched && meta.error !== undefined}
                  error={meta.error}
                  style={{ marginBottom: 10 }}
                >
                  <EuiFieldText
                    id={fieldKey}
                    title={fieldName}
                    name={fieldKey}
                    value={values[fieldKey]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </EuiFormRow>
              );
            } else {
              return null;
            }

          case "interest":
            return (
              <EuiFormRow
                key={fieldKey}
                title={fieldName}
                label={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <EuiSelect
                  id={fieldKey}
                  name={fieldKey}
                  title={fieldName}
                  onChange={handleChange}
                  hasNoInitialSelection
                  onBlur={handleBlur}
                  value={values[fieldKey]}
                  options={[
                    ...PERSONAL_SPACE_FORM_INTEREST_OPTIONS.map((option) => ({
                      value: option.key,
                      text: option.display,
                    })),
                  ]}
                />
              </EuiFormRow>
            );
          case "country":
            return (
              <EuiFormRow
                key={fieldKey}
                title={fieldName}
                label={fieldName}
                isInvalid={meta.touched && meta.error !== undefined}
                error={meta.error}
                style={{ marginBottom: 10 }}
              >
                <CountrySelectable
                  value={values[fieldKey]}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                />
              </EuiFormRow>
            );
          case "state":
            if (
              (stateRequiredCountries as string[]).includes(
                getFieldMeta<PersonalSpaceFormFields["country"]>(
                  "country" as const satisfies keyof PersonalSpaceFormFields,
                )?.value,
              )
            ) {
              return stateRequiredCountries
                .filter(
                  (country) =>
                    country ===
                    getFieldMeta<PersonalSpaceFormFields["country"]>(
                      "country" as const satisfies keyof PersonalSpaceFormFields,
                    )?.value,
                )
                .map((country) => {
                  switch (country) {
                    // switch just to make sure we handle all countries
                    case "US":
                    case "CA":
                      return (
                        <EuiFormRow
                          key={fieldKey}
                          title={fieldName}
                          label={fieldName}
                          isInvalid={meta.touched && meta.error !== undefined}
                          error={meta.error}
                          style={{ marginBottom: 10 }}
                        >
                          <StateProvinceSelectable
                            country={country}
                            value={values[fieldKey] as StateCode | ProvinceCode}
                            setFieldValue={setFieldValue}
                          />
                        </EuiFormRow>
                      );
                  }
                  return null;
                });
            } else {
              return null;
            }
        }
        return null;
      })}
    </div>
  );
};
